import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Button,
  Pagination,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Divider,
  Container,
  Stack,
} from "@mui/material";
import { Delete, FileDownload as FileDownloadIcon } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import apiClient from "../../api/apiClient";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import { formatDate } from "../../utils/dateUtils";
import { useSnackbar } from "../../context/SnackbarProvider";
import BreadcrumbsNav from "../../components/BreadcrumbsNav";

const ViewRegistrations = () => {
  const { eventslug } = useParams();
  const [registrations, setRegistrations] = useState([]);
  const [eventDetails, setEventDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [registrationToDelete, setRegistrationToDelete] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalRegistrations, setTotalRegistrations] = useState(0);
  const [isPublicEvent, setIsPublicEvent] = useState(false);

  const showSnackbar = useSnackbar();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const eventResponse = await apiClient.get(`/events/slug/${eventslug}`);
        setEventDetails(eventResponse.data);
        setIsPublicEvent(eventResponse.data.eventType === "public");

        const registrationsResponse = await apiClient.get(
          `/registrations/event/${eventslug}?page=${page}&limit=${limit}`
        );

        setRegistrations(registrationsResponse.data.data || []);
        setTotalRegistrations(
          registrationsResponse.data.pagination.totalRegistrations || 0
        );
      } catch (err) {
        if (err.response?.status === 400 || err.response?.status === 404) {
          showSnackbar(
            err.response?.data?.message || "Event not found.",
            "error"
          );
        } else {
          showSnackbar("An unexpected error occurred.", "error");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleDelete = async () => {
    try {
      await apiClient.delete(`/registrations/${registrationToDelete}`);
      setRegistrations((prev) =>
        prev.filter((reg) => reg._id !== registrationToDelete)
      );
      setTotalRegistrations((prev) => prev - 1);
      setRegistrationToDelete(null);
    } catch (err) {
      showSnackbar(`Failed to delete registration.`, "error");
    }
  };

  const handlePageChange = (_, value) => {
    setPage(value);
  };

  const handleLimitChange = (event) => {
    setLimit(event.target.value);
    setPage(1);
  };

  const exportToCSV = () => {
    if (!eventDetails) return;

    const csvHeaders = isPublicEvent
      ? ["Name", "Email", "Phone", "Company"]
      : ["Employee ID", "Employee Name", "Table Number", "Table Image URL"];

    const csvRows = registrations.map((reg) =>
      isPublicEvent
        ? [
            reg.employeeName || "N/A",
            reg.email || "N/A",
            reg.phone || "N/A",
            reg.company || "N/A",
          ]
        : [
            reg.employeeId || "N/A",
            reg.employeeName || "N/A",
            reg.tableNumber || "N/A",
            reg.tableImage || "N/A",
          ]
    );

    const eventMetadata = [
      ["Event Short Name:", eventDetails.slug || "N/A"],
      ["Event Name:", eventDetails.name || "N/A"],
      ["Event Date:", formatDate(eventDetails.date || "N/A")],
      ["Venue:", eventDetails.venue || "N/A"],
      ["Description:", eventDetails.description || "N/A"],
      ["Logo URL:", eventDetails.logoUrl || "N/A"],
      ["Event Type:", eventDetails.eventType || "N/A"],
      [],
    ];

    const csvContent = [
      ...eventMetadata.map((row) => row.join(",")),
      csvHeaders.join(","),
      ...csvRows.map((row) => row.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${eventDetails.name || "event"}_registrations.csv`;
    link.click();
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "calc(100vh - 120px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 6, mb: 4 }}>
      <BreadcrumbsNav></BreadcrumbsNav>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems="flex-start"
        mb={2}
        spacing={2}
      >
        {/* Left Side: Event Details */}
        <Box flex={1}>
          <Typography variant="h4" fontWeight="bold">
            Event Details
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "text.secondary", textAlign: "left" }}
          >
            View event details and manage registrations for this event. Export
            registration data or delete entries as needed.
          </Typography>
        </Box>

        {/* Right Side: Registrations and Export */}
        <Box textAlign="right">
          <Typography
            variant="h6"
            sx={{ mb: 2, fontWeight: "medium", color: "text.secondary" }}
          >
            Total Registrations: {totalRegistrations}
          </Typography>
          {totalRegistrations > 0 && (
            <Button
              variant="contained"
              sx={{ mb: 0 }}
              onClick={exportToCSV}
              color="primary"
              startIcon={<FileDownloadIcon />}
            >
              Export to CSV
            </Button>
          )}
        </Box>
      </Stack>
      <Divider sx={{ width: "100%", mt: 2, mb: 4 }} />

      {eventDetails && (
        <Box
          sx={{
            width: "100%",
            maxWidth: 600,
            mb: 3,
            p: 4,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            bgcolor: "background.paper",
            mx: "auto",
          }}
        >
          {eventDetails.logoUrl && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <img
                src={eventDetails.logoUrl}
                alt={`${eventDetails.name} Logo`}
                style={{
                  maxWidth: "250px",
                  height: "auto",
                  objectFit: "contain",
                  borderRadius: "8px",
                }}
              />
            </Box>
          )}
          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
            {eventDetails.name}
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary", mb: 0.5 }}>
            <strong>Date:</strong> {formatDate(eventDetails.date)}
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary", mb: 0.5 }}>
            <strong>Venue:</strong> {eventDetails.venue}
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary", mb: 0.5 }}>
            <strong>Type:</strong> {eventDetails.eventType}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            <strong>Description:</strong> {eventDetails.description || "N/A"}
          </Typography>
        </Box>
      )}

      <Divider sx={{ my: 3, width: "100%" }} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          width: "100%",
          px: 2,
        }}
      >
        <Typography variant="body1">
          Showing {Math.min((page - 1) * limit + 1, totalRegistrations)}–
          {Math.min(page * limit, totalRegistrations)} of {totalRegistrations}{" "}
          records
        </Typography>
        <FormControl size="small" sx={{ minWidth: 150, ml: 2 }}>
          <InputLabel id="limit-select-label">Records per page</InputLabel>
          <Select
            labelId="limit-select-label"
            value={limit}
            onChange={handleLimitChange}
            label="Records per page"
          >
            {[5, 10, 20, 50, 100, 250, 500].map((value) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {registrations.length === 0 ? (
        <Typography
          sx={{
            mt: 2,
            fontSize: "1.2rem",
            color: "text.secondary",
            textAlign: "center",
          }}
        >
          No registrations found for this event.
        </Typography>
      ) : (
        <>
          <Grid container spacing={4}>
            {registrations.map((registration) => (
              <Grid item xs={12} sm={6} md={4} key={registration._id}>
                <Card
                  sx={{
                    boxShadow: 3,
                    borderRadius: 2,
                    height: "100%",
                    position: "relative",
                  }}
                >
                  <IconButton
                    color="error"
                    sx={{
                      position: "absolute",
                      top: "10px",
                      right: "10px",
                    }}
                    onClick={() => {
                      setRegistrationToDelete(registration._id);
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <Delete />
                  </IconButton>
                  <CardContent>
                    {isPublicEvent ? (
                      <>
                        <Typography variant="h6" gutterBottom>
                          {registration.fullName}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ mt: 1 }}
                        >
                          <strong>Email:</strong> {registration.email}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ mt: 1 }}
                        >
                          <strong>Phone:</strong> {registration.phone}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ mt: 1 }}
                        >
                          <strong>Company:</strong>{" "}
                          {registration.company || "N/A"}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Typography variant="h6" gutterBottom>
                          {registration.employeeName}
                        </Typography>
                        {registration.employeeId && (
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{ mt: 1 }}
                          >
                            <strong>Employee ID:</strong>{" "}
                            {registration.employeeId}
                          </Typography>
                        )}
                        {registration.tableNumber && (
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{ mt: 1 }}
                          >
                            <strong>Table Number:</strong>{" "}
                            {registration.tableNumber}
                          </Typography>
                        )}
                        {registration.tableImage && (
                          <Box
                            sx={{
                              mt: 2,
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            <img
                              src={registration.tableImage}
                              alt="Table"
                              style={{
                                maxWidth: "250px",
                                height: "auto",
                                objectFit: "contain",
                                borderRadius: "8px",
                              }}
                            />
                          </Box>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <Pagination
              count={Math.ceil(totalRegistrations / limit)}
              page={page}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        </>
      )}

      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Delete Registration"
        message="Are you sure you want to delete this registration?"
      />
    </Container>
  );
};

export default ViewRegistrations;
